import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetCategories } from "../../hooks/categories/useGetCategories";
import { useDeleteCategory } from "../../hooks/categories/useDeleteCategory";
import { useGetDepartments } from "../../hooks/Departments/useGetDepartments";
import { useGetCategoriesForSelect } from "../../hooks/categories/useGetCategoriesForSelect";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import CategoriesTableRow from "./Table/CategoriesTableRow";
import { CategoriesTableColumns } from "./Table/CategoriesTableColumns";
import { usePermissions } from "../../auth/PermissionsContext";

const CategoriesTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("categories_list"),
            create: has("categories_create"),
            edit: has("categories_edit"),
            delete: has("categories_delete"),
        }),
        [has]
    );

    const navigate = useNavigate();

    // Filter States
    const [departmentFilter, setDepartmentFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [parentFilter, setParentFilter] = useState("");

    // CRUD Hooks
    const { deleteCat, isDelete } = useDeleteCategory();

    // جلب القطاعات للفلاتر
    const { departments } = useGetDepartments({ per_page: 100 });
    const { options: categoryOptions } = useGetCategoriesForSelect({
        department_id: departmentFilter || undefined
    });

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/categories",
        deleteSingle: deleteCat,
    });

    const params = useMemo(
        () => ({
            page,
            per_page: perPage,
            search: search || undefined,
            department_id: departmentFilter || undefined,
            parent_id: parentFilter || undefined,
            is_active: statusFilter !== "" ? statusFilter : undefined,
            sort_by: 'created_at',
            sort_order: order,
        }),
        [page, perPage, search, departmentFilter, parentFilter, statusFilter, order]
    );

    const { isLoading, error, categories, meta } = useGetCategories(params);

    // تجهيز خيارات القطاعات للفلتر
    const departmentOptions = useMemo(() => {
        const baseOptions = [{ value: "", label: { ar: "كل القطاعات", en: "All Departments" } }];
        const deps = departments.map(dep => ({
            value: dep.id,
            label: {
                ar: dep.name?.ar || dep.name?.en,
                en: dep.name?.en || dep.name?.ar
            }
        }));
        return [...baseOptions, ...deps];
    }, [departments]);

    // تجهيز الفلاتر لـ TableLayout
    const tableFilters = [
        {
            name: "department",
            label: { ar: "القطاع", en: "Department" },
            value: departmentFilter,
            onChange: (val) => {
                setDepartmentFilter(val);
                setParentFilter(""); // Reset parent when department changes
            },
            options: departmentOptions
        },
        {
            name: "parent",
            label: { ar: "الفئة الأب", en: "Parent Category" },
            value: parentFilter,
            onChange: (val) => setParentFilter(val),
            options: [{ value: "", label: { ar: "الكل", en: "All" } }, ...categoryOptions.map(cat => ({
                value: cat.id,
                label: { ar: cat.name?.ar || cat.name?.en, en: cat.name?.en || cat.name?.ar }
            }))]
        },
        {
            name: "status",
            label: { ar: "الحالة", en: "Status" },
            value: statusFilter,
            onChange: (val) => setStatusFilter(val),
            options: [
                { value: "", label: { ar: "الكل", en: "All" } },
                { value: "1", label: { ar: "نشط", en: "Active" } },
                { value: "0", label: { ar: "غير نشط", en: "Inactive" } },
            ]
        }
    ];

    const renderRow = (rowParams) => (
        <CategoriesTableRow
            key={rowParams.item.id}
            category={rowParams.item}
            index={rowParams.index}
            isSelected={selectedIds.includes(rowParams.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
        />
    );

    const title = isArabic ? "الفئات" : "Categories";
    const subtitle = isArabic ? "إدارة فئات المنتجات" : "Manage product categories";

    return (
        <TableLayout
            title={title}
            subtitle={subtitle}
            search={search}
            onSearchChange={setSearch}
            addButtonLabel={isArabic ? "إضافة فئة" : "Add Category"}
            onAddClick={() => navigate("/categories/create")}
            addButtonVisible={perms.create}
            isLoading={isLoading}
            error={error}
            items={categories}
            meta={meta}
            page={page}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            order={order}
            onRequestSort={handleRequestSort}
            columns={CategoriesTableColumns}
            renderRow={renderRow}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            menuDisableEdit={!perms.edit}
            menuDisableDelete={!perms.delete}
            isDeletingSingle={isDelete}
            selectedIds={selectedIds}
            onSelectAllClick={(e) => handleSelectAllClick(e, categories)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onBulkDelete={handleBulkDelete}
            bulkDeleteDisabled={!perms.delete}
            filters={tableFilters}
            onClearAllFilters={() => {
                setDepartmentFilter("");
                setParentFilter("");
                setStatusFilter("");
            }}
            emptyLabelAr={isArabic ? "لا توجد فئات" : "No categories"}
            emptyLabelEn={isArabic ? "لا توجد فئات" : "No categories"}
        />
    );
};

export default CategoriesTable;
